import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, map, mergeMap, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { AuthApiService } from './auth-api.service';
import { Auth0Service } from '../../services/auth0.service';
import { Store } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import { CookieService } from 'ngx-cookie-service';
import * as fromAuth from './';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { Location } from '@angular/common';
import * as authSelectors from './auth.selectors';
import { LanguageApiService } from 'src/app/modules/core/languages/language-api.service';
import { LabelTranslationService } from 'src/app/modules/core/languages/label-translation.service';
import Environment from '@env';
import { concatLatestFrom } from '@ngrx/operators';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authApiService: AuthApiService,
    private auth0Service: Auth0Service,
    private store: Store<any>,
    private cookieService: CookieService,
    private location: Location,
    private languageApiService: LanguageApiService,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    private labelTranslationService: LabelTranslationService
  ) {}

  loginStepEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.loginStepEmail),
      mergeMap(action => {
        const username = action.loginData.username;
        const cultureCode = action.loginData.cultureCode;

        return this.authApiService.getConnection(action.loginData.username).pipe(
          map(connection => fromAuth.getConnectionSuccess({ connection, username, cultureCode })),
          catchError(e => {
            return of(fromAuth.getConnectionFailed({ err: e }));
          })
        );
      })
    )
  );

  getConnectionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.getConnectionSuccess),
      switchMap(action => {
        if (!action.connection) {
          return of(fromAuth.setError({ err: { message: 'ERROR_ACCOUNT_DOES_NOT_EXIST' } }));
        }

        const { connection, username, cultureCode } = action;

        if (connection.connectionId === 'Username-Password-Authentication') {
          this.store.dispatch(fromUserProfile.setConnection({ profileConnection: connection.connectionId }));
          return of(fromAuth.showPassword({ connection, username }));
        }

        return of(fromAuth.startAuth0Connection({ connection, cultureCode }));
      })
    )
  );

  startAuth0Connection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.startAuth0Connection),
        tap(action => {
          const state = {
            random: uuidv4(),
            cultureCode: action.cultureCode
          };
          this.auth0Service.login('/login', action.connection.connectionId, state);
        })
      ),
    { dispatch: false }
  );

  processAuth0LoginResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.processAuth0LoginResult),
      mergeMap(action =>
        this.authApiService.getAuthenticatedToken(action.code, action.state).pipe(
          switchMap(token => {
            const userContext = new fromAuth.UserContext(token.access_token);
            return [
              fromAuth.setToken({ token }),
              fromAuth.processAuth0LoginResultSuccess(),
              fromUserProfile.getUserProfile(),
              fromUserProfile.getUserOrganizations(),
              fromUserProfile.getUserOrganizationLanguages(),
              fromUserProfile.getUserBusinessUnits(),
              fromUserProfile.getUserOrganization(),
              fromAuth.routeAfterLoginSuccess({ userContext: userContext, checkPolicy: true })
            ];
          }),
          catchError(e => {
            return of(fromAuth.getConnectionFailed({ err: e }));
          })
        )
      )
    )
  );

  processHorizonLoginResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.processHorizonLoginResult),
      mergeMap(action =>
        this.authApiService.getTokenForHorizonAuthentication(action.jwt, action.org).pipe(
          switchMap(token => {
            const userContext = new fromAuth.UserContext(token.access_token);
            return [
              fromAuth.setToken({ token }),
              fromAuth.processHorizonLoginResultSuccess(),
              fromUserProfile.getUserProfile(),
              fromUserProfile.getUserOrganizations(),
              fromUserProfile.getUserOrganizationLanguages(),
              fromUserProfile.getUserBusinessUnits(),
              fromUserProfile.getUserOrganization(),
              fromAuth.routeAfterLoginSuccess({ userContext: userContext, checkPolicy: true })
            ];
          }),
          catchError(e => {
            return of(fromAuth.getConnectionFailed({ err: e }));
          })
        )
      )
    )
  );

  processHorizonRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.processHorizonRedirect),
      mergeMap(action =>
        this.authApiService.getHorizonRedirect().pipe(
          switchMap(path => {
            return [fromAuth.processHorizonRedirectSuccess({ path })];
          })
        )
      )
    )
  );

  processHorizonRedirectSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.processHorizonRedirectSuccess),
        switchMap(action => {
          let url = action.path?.redirectPath;

          if (url) {
            window.open(url);
          }
          return [];
        })
      ),
    { dispatch: false }
  );

  changeOrganization = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.changeOrganization),
      mergeMap(action =>
        this.authApiService.getAuthenticatedTokenByOrganization(action.organizationUId).pipe(
          switchMap(token => {
            return [
              fromRouting.go({
                routeData: {
                  path: ['change-organization'],
                  extras: { state: { newToken: token, organizationUId: action.organizationUId } }
                }
              })
            ];
          }),
          catchError(e => {
            return of(fromAuth.getConnectionFailed({ err: e }));
          })
        )
      )
    )
  );

  changingOrganization = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.changingOrganization),
      switchMap(token => {
        const userContext = new fromAuth.UserContext(token.token.access_token);
        return [
          fromAuth.clearAllStates(),
          fromAuth.loadAuthTokenByOrganization(token),
          fromUserProfile.getUserProfile(),
          fromUserProfile.getUserOrganizations(),
          fromUserProfile.getUserOrganizationLanguages(),
          fromUserProfile.getUserBusinessUnits(),
          fromUserProfile.getUserOrganization(),
          fromAuth.routeAfterLoginSuccess({ userContext, checkPolicy: false })
        ];
      })
    )
  );

  reloadBrowser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.reloadBrowser),
        map(() => {
          document.location.href = '/cop';
        })
      ),
    { dispatch: false }
  );

  loginUserNamePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.loginUserNamePassword),
      mergeMap(action => {
        return this.authApiService
          .authenticateUsernamePassword(action.loginData.username, action.loginData.password, action.loginData.cultureCode)
          .pipe(
            switchMap(token => {
              const userContext = new fromAuth.UserContext(token.access_token);
              return [
                fromAuth.setToken({ token }),
                fromAuth.loginUserNamePasswordSuccess(),
                fromUserProfile.getUserProfile(),
                fromUserProfile.getUserOrganizations(),
                fromUserProfile.getUserOrganizationLanguages(),
                fromUserProfile.getUserBusinessUnits(),
                fromUserProfile.getUserOrganization(),
                fromAuth.routeAfterLoginSuccess({ userContext, checkPolicy: true })
              ];
            }),
            catchError(e => {
              return of(fromAuth.setError({ err: e }));
            })
          );
      })
    )
  );

  routeAfterLoginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.routeAfterLoginSuccess),
      withLatestFrom(this.store.select(authSelectors.selectBusinessUnitsWarningAccepted)),
      switchMap(([action, acceptedWarning]) => {
        const userContext = action.userContext;

        if (action.checkPolicy === true && userContext.acceptedPolicy !== true) {
          this.store.dispatch(fromAuth.showAcceptancePage());
          return [];
        }

        if (userContext.needsBusinessUnit === true && !acceptedWarning) {
          this.store.dispatch(fromAuth.showBusinessUnitsWarning());
          return [];
        }

        const route = !!userContext.defaultRoute && userContext.defaultRoute.trim().length ? userContext.defaultRoute : 'cop';

        if (route === 'cop') {
          // Set a deep link cookie to use for routing on successful login
          let url = this.cookieService.get('deeplink-url');

          if (url) {
            this.cookieService.delete('deeplink-url', '/');
            return [
              fromRouting.go({
                routeData: { path: [url] }
              })
            ];
          }
        }

        return [
          fromRouting.go({
            routeData: { path: [route] }
          })
        ];
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.logout),
      switchMap(action => {
        // Set a deep link cookie to use for routing on successful login
        let url = this.location.path();

        // Deep link via the login page expires after 10 minutes
        let expires = new Date();
        expires.setMinutes(expires.getMinutes() + 10);

        this.cookieService.delete('deeplink-url', '/');
        this.cookieService.set('deeplink-url', url, { path: '/', expires: expires });

        return this.authApiService.logout().pipe(
          switchMap(() => {
            return [
              fromAuth.logoutSuccess(),
              fromRouting.go({
                routeData: { path: ['login'] }
              })
            ];
          })
        );
      })
    )
  );

  refreshAccessToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.refreshAccessToken),
      switchMap(action => {
        return this.authApiService.refreshAccessToken().pipe(
          switchMap(token => {
            if (!token) {
              return of(fromAuth.logout());
            }

            return [fromAuth.setToken({ token })];
          }),
          catchError(e => {
            return of(fromAuth.logout());
          })
        );
      })
    )
  );

  sendPasswordResetLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.sendPasswordResetLink),
      switchMap(action => {
        return this.authApiService.sendPasswordResetLink(action.username).pipe(
          switchMap(() => {
            return [
              fromUserProfile.profileLogout(),
              fromAuth.logoutSuccess(),
              fromRouting.go({
                routeData: { path: ['login'] }
              })
            ];
          })
        );
      })
    )
  );

  isResetCodeExpired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.isResetCodeExpired),
      switchMap(action => {
        return this.authApiService.isResetCodeExpired(action.username, action.authKey).pipe(
          switchMap(exp => {
            return [fromAuth.setExpired({ expirationStatus: exp })];
          }),
          catchError(e => {
            return of(fromAuth.setError({ err: e }));
          })
        );
      })
    )
  );

  isInviteCodeExpired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.isInviteCodeExpired),
      switchMap(action => {
        return this.authApiService.isInviteCodeExpired(action.username, action.authKey).pipe(
          switchMap(exp => {
            return [fromAuth.setExpired({ expirationStatus: exp })];
          }),
          catchError(e => {
            return of(fromAuth.setError({ err: e }));
          })
        );
      })
    )
  );

  setUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.setUserPassword),
      switchMap(action => {
        return this.authApiService.setUserPassword(action.username, action.newPassword, action.authKey).pipe(
          switchMap(() => {
            return [
              fromRouting.go({
                routeData: { path: ['login'] }
              }),
              fromAuth.setError({ err: null })
            ];
          }),
          catchError(e => {
            return of(fromAuth.setError({ err: e }));
          })
        );
      })
    )
  );

  setNewUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.setNewUserPassword),
      switchMap(action => {
        return this.authApiService.setNewUserPassword(action.username, action.newPassword, action.authKey).pipe(
          switchMap(() => {
            return [
              fromRouting.go({
                routeData: { path: ['login'] }
              }),
              fromAuth.setError({ err: null })
            ];
          }),
          catchError(e => {
            return of(fromAuth.setError({ err: e }));
          })
        );
      })
    )
  );

  resendInviteLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.resendInviteLink),
      switchMap(action => {
        return this.authApiService.resendInviteLink(action.username).pipe(
          switchMap(() => {
            return [
              fromRouting.go({
                routeData: { path: ['login'] }
              })
            ];
          })
        );
      })
    )
  );

  routeToSharedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.routeToSharedMap),
      switchMap(action => {
        return this.authApiService.sharedMapAuth(action.sharedCode).pipe(
          switchMap(token => {
            if (token.authTokenDto) {
              return [
                fromAuth.setToken({ token: token.authTokenDto }),
                fromUserProfile.getUserProfile(),
                fromUserProfile.getUserOrganizationLanguages(),
                fromUserProfile.getUserBusinessUnits(),
                fromUserProfile.getUserOrganization(),
                fromRouting.go({
                  routeData: { path: [action.target, token.mapRedirectPath] }
                }),
                fromAuth.sharedMapAuthWorking({ isWorking: false })
              ];
            }

            return [
              fromRouting.go({
                routeData: { path: [action.target, token.mapRedirectPath] }
              }),
              fromAuth.sharedMapAuthWorking({ isWorking: false })
            ];
          }),
          catchError(e => {
            return of(fromAuth.setMapLinkError({ err: e }));
          })
        );
      })
    )
  );

  loadAvailableLanguagesForLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.loadAvailableLanguagesForLogin),
      switchMap(action => {
        return this.languageApiService.getLoginLanguages().pipe(
          map(codes => fromAuth.loadAvailableLanguagesComplete({ languages: codes })),
          catchError(err => of(fromAuth.setError({ err })))
        );
      })
    )
  );

  routeToSharedDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.routeToSharedDashboard),
      switchMap(action => {
        return this.authApiService.sharedDashboardAuth(action.sharedCode).pipe(
          switchMap(token => {
            if (token.authTokenDto) {
              return [
                fromAuth.setToken({ token: token.authTokenDto }),
                fromUserProfile.getUserProfile(),
                fromUserProfile.getUserOrganizationLanguages(),
                fromUserProfile.getUserBusinessUnits(),
                fromUserProfile.getUserOrganization(),
                fromRouting.go({
                  routeData: { path: ['analytics', token.redirectPath] }
                }),
                fromAuth.sharedDashboardAuthWorking({ isWorking: false })
              ];
            }

            return [
              fromRouting.go({
                routeData: { path: ['analytics', token.redirectPath] }
              }),
              fromAuth.sharedDashboardAuthWorking({ isWorking: false })
            ];
          }),
          catchError(e => {
            return of(fromAuth.setDashboardLinkError({ err: e }));
          })
        );
      })
    )
  );

  routeToExternalLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.routeToExternalLink),
      switchMap(action => {
        return this.authApiService.externalLinkAuth(action.sharedCode).pipe(
          switchMap(token => {
            if (token.authToken) {
              const actions = [];
              actions.push(fromAuth.setToken({ token: token.authToken }));
              actions.push(fromUserProfile.getUserProfile());
              actions.push(fromUserProfile.getUserOrganizationLanguages());
              actions.push(fromUserProfile.getUserBusinessUnits());
              actions.push(fromUserProfile.getUserOrganization());
              if (token.isDirectUrl !== true) {
                actions.push(
                  fromRouting.navigateToUrl({
                    urlNavigationData: { url: token.redirectPath, extras: { skipLocationChange: token.silentRedirect === true } }
                  })
                );
              }
              actions.push(fromAuth.externalLinkAuthWorking({ isWorking: false }));

              if (token.isDirectUrl === true) {
                window.location.href = token.redirectPath;
              }
              return actions;
            }

            return [
              fromRouting.go({
                routeData: { path: [token.redirectPath] }
              }),
              fromAuth.externalLinkAuthWorking({ isWorking: false })
            ];
          }),
          catchError(e => {
            return of(fromAuth.setExternalLinkError({ err: e }));
          })
        );
      })
    )
  );

  private synchronizeCookieToken(authToken: fromAuth.AuthToken) {
    const token = authToken?.access_token ?? null;

    if (!!token) {
      const cookieToken = this.cookieService.get('token');
      if (token !== cookieToken) {
        this.cookieService.set('token', token, {
          path: '/',
          sameSite: 'Strict',
          secure: Environment.production
        });
      }
    } else {
      this.cookieService.delete('token');
    }
  }

  checkTokenSyncOnInit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        concatLatestFrom(() => this.store.select(fromAuth.selectToken)),
        tap(([, token]) => {
          this.synchronizeCookieToken(token);
        })
      ),
    { dispatch: false }
  );

  synchronizeTokens$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.setToken),
        tap(action => {
          this.synchronizeCookieToken(action.token);
        })
      ),
    { dispatch: false }
  );

  // reducer for these actions sets the token to null without calling the setToken action.
  clearTokens$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.logoutSuccess, fromAuth.resetLogin),
        tap(() => {
          this.synchronizeCookieToken(null);
        })
      ),
    { dispatch: false }
  );

  getZendeskSsoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.getZendeskSsoSuccess),
        switchMap(action => {
          let url = `https://crisis24.zendesk.com/access/jwt?jwt=${action.ssoToken.token}&return_to=topoone.zendesk.com`;
          console.log(url);
          window.open(url);
          return [];
        })
      ),
    { dispatch: false }
  );

  getZendeskSso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.getZendeskSso),
      mergeMap(action =>
        this.authApiService.getZendeskSso().pipe(
          switchMap(ssoToken => {
            return [fromAuth.getZendeskSsoSuccess({ ssoToken })];
          })
        )
      )
    )
  );

  getLitmosSso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.getLitmosSso),
      mergeMap(action =>
        this.authApiService.getLitmosSso().pipe(
          switchMap(loginKeyResponse => {
            return [fromAuth.getLitmosSsoSuccess({ getLoginKeyResponse: loginKeyResponse })];
          })
        )
      )
    )
  );

  getLitmosSsoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.getLitmosSsoSuccess),
      switchMap(action => {
        if (!action.getLoginKeyResponse) {
          this.notify.error(this.translate.instant(`ERRORS.FAILED_TO_OPEN_TRAINING`));
          return [];
        }
        if (action.getLoginKeyResponse?.errorMessage) {
          this.notify.error(action.getLoginKeyResponse?.errorMessage);
          return [];
        }
        if (action.getLoginKeyResponse?.loginKey) {
          window.open(action.getLoginKeyResponse.loginKey);
        }
        return [];
      }),
      catchError(e => {
        console.log(e);
        return of(fromAuth.setError({ err: e }));
      })
    )
  );
}
